import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Cookie, X } from "lucide-react";

const STORAGE_KEY = "fastblog_cookie_consent";
const CONSENT_VERSION = 1;

export type CookieConsent = {
  version: number;
  essential: true;
  analytics: boolean;
  marketing: boolean;
  ts: number;
};

export function getCookieConsent(): CookieConsent | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.version !== CONSENT_VERSION) return null;
    return parsed as CookieConsent;
  } catch {
    return null;
  }
}

function saveConsent(consent: Omit<CookieConsent, "version" | "essential" | "ts">) {
  const data: CookieConsent = {
    version: CONSENT_VERSION,
    essential: true,
    analytics: consent.analytics,
    marketing: consent.marketing,
    ts: Date.now(),
  };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {}
  window.dispatchEvent(new CustomEvent("cookie-consent-change", { detail: data }));
}

export default function CookieConsentBanner() {
  const [visible, setVisible] = useState(false);
  const [customizeOpen, setCustomizeOpen] = useState(false);
  const [analytics, setAnalytics] = useState(true);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => {
      if (!getCookieConsent()) setVisible(true);
    }, 600);
    return () => clearTimeout(t);
  }, []);

  if (!visible) return null;

  const acceptAll = () => {
    saveConsent({ analytics: true, marketing: true });
    setVisible(false);
  };
  const acceptEssential = () => {
    saveConsent({ analytics: false, marketing: false });
    setVisible(false);
  };
  const saveCustom = () => {
    saveConsent({ analytics, marketing });
    setCustomizeOpen(false);
    setVisible(false);
  };

  return (
    <>
      <div
        role="dialog"
        aria-labelledby="cookie-banner-title"
        className="fixed inset-x-0 bottom-0 z-[100] p-3 md:p-4 pointer-events-none"
      >
        <div className="mx-auto max-w-4xl pointer-events-auto rounded-2xl border border-border bg-background/95 backdrop-blur-md shadow-lg p-4 md:p-5">
          <div className="flex items-start gap-3">
            <div className="hidden sm:flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[hsl(330,80%,60%)]/10">
              <Cookie className="h-5 w-5 text-[hsl(330,80%,60%)]" />
            </div>
            <div className="flex-1 min-w-0">
              <h2 id="cookie-banner-title" className="text-sm md:text-base font-semibold text-[#a527be]">
                Sua privacidade importa
              </h2>
              <p className="mt-1 text-xs md:text-sm text-muted-foreground leading-relaxed">
                Usamos cookies essenciais para manter seu login e funcionalidades básicas, e cookies opcionais para melhorar sua experiência.
                Saiba mais na nossa{" "}
                <Link to="/politica-de-privacidade" className="underline text-[hsl(330,80%,60%)] hover:text-[hsl(330,80%,55%)]">
                  Política de Privacidade
                </Link>
                .
              </p>
              <div className="mt-3 flex flex-col sm:flex-row gap-2">
                <Button
                  size="sm"
                  className="bg-[hsl(330,80%,60%)] hover:bg-[hsl(330,80%,55%)] text-white"
                  onClick={acceptAll}
                >
                  Aceitar todos
                </Button>
                <Button size="sm" variant="outline" onClick={acceptEssential}>
                  Apenas essenciais
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setCustomizeOpen(true)}>
                  Personalizar
                </Button>
              </div>
            </div>
            <button
              aria-label="Fechar"
              className="text-muted-foreground hover:text-foreground transition-colors"
              onClick={acceptEssential}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      <Dialog open={customizeOpen} onOpenChange={setCustomizeOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Preferências de cookies</DialogTitle>
            <DialogDescription>
              Escolha quais categorias de cookies você permite. Você pode mudar essa escolha a qualquer momento.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="flex items-start justify-between gap-3 rounded-lg border p-3">
              <div>
                <div className="text-sm font-medium">Essenciais</div>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Necessários para login, segurança e funcionalidades básicas. Não podem ser desativados.
                </p>
              </div>
              <Switch checked disabled />
            </div>
            <div className="flex items-start justify-between gap-3 rounded-lg border p-3">
              <div>
                <div className="text-sm font-medium">Analytics</div>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Nos ajudam a entender como você usa o app para melhorá-lo.
                </p>
              </div>
              <Switch checked={analytics} onCheckedChange={setAnalytics} />
            </div>
            <div className="flex items-start justify-between gap-3 rounded-lg border p-3">
              <div>
                <div className="text-sm font-medium">Marketing</div>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Personalizam comunicações e ofertas relevantes para você.
                </p>
              </div>
              <Switch checked={marketing} onCheckedChange={setMarketing} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={acceptEssential}>
              Apenas essenciais
            </Button>
            <Button
              className="bg-[hsl(330,80%,60%)] hover:bg-[hsl(330,80%,55%)] text-white"
              onClick={saveCustom}
            >
              Salvar preferências
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}